/* import __COLOCATED_TEMPLATE__ from './phone-number-selector.hbs'; */
/* RESPONSIBLE TEAM: team-proactive-support */
import Component from '@glimmer/component';
import { action } from '@ember/object';
import { inject as service } from '@ember/service';
import { countryData } from 'embercom/components/outbound/sms/onboarding/country-data';
import type Store from '@ember-data/store';
import type PhoneNumber from 'embercom/models/sms/phone-number';

interface PhoneNumberWithSelection {
  phoneNumber: PhoneNumber;
  isSelected: boolean;
}

interface CountryGroup {
  countryCode: string;
  countryName: string;
  items: PhoneNumberWithSelection[];
}

interface Args {
  selectedPhoneNumberIds: number[] | undefined;
  onSelectionChange: (ids: number[]) => void;
}

interface Signature {
  Args: Args;
}

export default class SmsPhoneNumberSelector extends Component<Signature> {
  @service declare store: Store;
  @service declare intercomEventService: $TSFixMe;

  get phoneNumbers(): PhoneNumber[] {
    return this.store.peekAll('sms/phone-number').slice() as PhoneNumber[];
  }

  get selectedIds(): number[] {
    return this.args.selectedPhoneNumberIds ?? [];
  }

  get countryGroups(): CountryGroup[] {
    let selectedSet = new Set(this.selectedIds);
    let grouped = new Map<string, PhoneNumberWithSelection[]>();

    for (let phone of this.phoneNumbers) {
      let existing = grouped.get(phone.countryCode) ?? [];
      existing.push({
        phoneNumber: phone,
        isSelected: selectedSet.has(Number(phone.id)),
      });
      grouped.set(phone.countryCode, existing);
    }

    let groups: CountryGroup[] = [];
    for (let [countryCode, items] of grouped) {
      let data = countryData[countryCode] as { name: string } | undefined;
      groups.push({
        countryCode,
        countryName: data?.name ?? countryCode,
        items,
      });
    }
    return groups;
  }

  get selectedSummary(): string {
    if (this.selectedIds.length === 0) {
      return '';
    }
    let selectedSet = new Set(this.selectedIds);
    let phones = this.phoneNumbers.filter((p) => selectedSet.has(Number(p.id)));
    return phones.map((p) => `${p.countryCode} ${p.number}`).join(', ');
  }

  get hasPhoneNumbers(): boolean {
    return this.phoneNumbers.length > 0;
  }

  @action trackDropdownOpened() {
    this.intercomEventService.trackAnalyticsEvent({
      action: 'opened',
      object: 'sms_sender_phone_number',
      section: 'outbound_editor',
      available_count: this.phoneNumbers.length,
      selected_count: this.selectedIds.length,
    });
  }

  @action handleKeydown(phoneNumber: PhoneNumber, event: KeyboardEvent) {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      this.togglePhoneNumber(phoneNumber);
    }
  }

  @action togglePhoneNumber(phoneNumber: PhoneNumber) {
    let id = Number(phoneNumber.id);
    let currentIds = [...this.selectedIds];

    if (currentIds.includes(id)) {
      currentIds = currentIds.filter((existingId) => existingId !== id);
    } else {
      // Replace any existing selection for the same country (one per country)
      let sameCountryIds = this.phoneNumbers
        .filter((p) => p.countryCode === phoneNumber.countryCode)
        .map((p) => Number(p.id));
      currentIds = currentIds.filter((existingId) => !sameCountryIds.includes(existingId));
      currentIds.push(id);
    }

    this.intercomEventService.trackAnalyticsEvent({
      action: currentIds.includes(id) ? 'selected' : 'deselected',
      object: 'sms_sender_phone_number',
      section: 'outbound_editor',
      country_code: phoneNumber.countryCode,
      selected_count: currentIds.length,
    });

    this.args.onSelectionChange(currentIds);
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Sms::PhoneNumberSelector': typeof SmsPhoneNumberSelector;
    'sms/phone-number-selector': typeof SmsPhoneNumberSelector;
  }
}
